import { GeoZoneHashElement, ZoneTypesValues } from "../../../context/map/fields/fieldsStore";
import {
    LeafletPixiApiSettings,
    MapRenderer,
    MapRendererConfig,
    PixiEntitySelection,
    RendererName
} from "./OperationRenderer";
import * as PIXI from "pixi.js";
import { hex2number } from "@utils/getColorByStatusUnit";
import PolygonDrawer, { PolyDrawOptions } from "@api/pixijs/drawers/PolygonDrawer";
import Logger from "@utils/logger";
import * as M from 'monocle-ts'
import { pipe } from "fp-ts/es6/function";
import { option } from "fp-ts";
import { Crop } from "@baseTypes/baseType";
import * as L from "leaflet";
import { Point, RendererOptions } from "leaflet";
import { CadastreFillType } from "@modules/map/tools/MapTools";
import { geoZoneSelectors } from "../../../store/reducers/geoZones";
import { store } from "../../../store";
import { EntityId } from "@reduxjs/toolkit";
import { ModeEnum } from "@modules/map/MapContainer";
import EventEmitter from "eventemitter3";
import EventNames = EventEmitter.EventNames;
import { UnitHashElement } from "@context/map/units/unitsStore";

export type GeoZoneGraphic = PIXI.Graphics & {}
const log = Logger.getLogger("geozone.renderer")
const getCropData = M.Optional.fromPath<GeoZoneHashElement>()(['dynamic', 'cropsInfo', 'cropData'])
const getColor = M.Optional.fromPath<Crop[]>()([0, 'color'])
const getCropDataColor = getCropData.compose(getColor)

const geoZoneColors: Record<ZoneTypesValues, string> = {
    'cadastre': '#b30000',
    'unused': '#CCCCCC',
    'field': '#CCFFCC',
    'viewOnly': '#CCFFCC',
    'home': '#CCFFCC',
    'other': '#CCFFCC',
} as const

export interface GeoZoneCadastreRendererOptions {
    fill?: CadastreFillType[number]
}

export interface GeoZoneRendererOptions extends RendererOptions {
    cadastre?: GeoZoneCadastreRendererOptions
}

export type GeoZoneRendererEventNames = {
    'geozoneedit': (selection: PixiEntitySelection) => void
    'select': (selection: PixiEntitySelection) => void
}

export class GeoZoneRenderer extends MapRenderer<GeoZoneHashElement, EntityId[], GeoZoneRendererEventNames> {

    getZIndex(): number {
        return this.zIndex
    }

    name = RendererName.GeoZone as const
    enabled: boolean = false
    container: PIXI.Container | undefined = undefined
    zIndex = 10
    opacity = .3

    constructor(config: MapRendererConfig, {children}: { children: MapRenderer<GeoZoneHashElement, EntityId[], GeoZoneRendererEventNames>[] }) {
        super(config)
        this.children = children
    }

    selected?: GeoZoneGraphic | undefined = undefined

    onOutsideClick() {
        super.onOutsideClick();
        if (this.selected) {
            this.selected.alpha = this.opacity
        }
        // this.service.refreshPixiContainer()
    }

    onPixiOverlayChange(settings: LeafletPixiApiSettings) {
        super.onPixiOverlayChange(settings);
        this.container?.children.forEach((graphic) => {
            const geoZoneGraphic = graphic as GeoZoneGraphic
            // this.redraw([geoZoneGraphic.name], { cadastre: { fill: 'Границы участка' } })
            this.scaleGeoZoneName(geoZoneGraphic, settings.scale || 1, settings.zoom!)
        })
    }

    scaleGeoZoneName(geoZoneGraphic: GeoZoneGraphic, scale: number, zoom: number) {
        const geoZoneNameGraphic: PIXI.Text = geoZoneGraphic.getChildByName('text') as PIXI.Text
        const geoZone = geoZoneSelectors.selectById(store.getState().geoZones, geoZoneGraphic.name)
        geoZoneNameGraphic.scale.x = 1 / scale;
        geoZoneNameGraphic.scale.y = 1 / scale;
        geoZoneNameGraphic.visible = geoZone?.zoneType === 'cadastre' ? zoom > 14 : zoom > 12
    }

    onGeoZoneRightClick(polygon: GeoZoneGraphic, geoZoneId: EntityId) {
        return () => {
            const geoZone = geoZoneSelectors.selectById(store.getState().geoZones, geoZoneId)
            if (geoZone) {
                log.debug("Right click on geoZone ", geoZone)
                // @ts-ignore
                const menu = this.map.contextmenu
                menu.removeAllItems()
                menu.addItem(
                    {
                        text: `Показать детали: ${geoZone.name} (${geoZone.glid})`,
                        callback: () => {
                            this.emit('select', { selectedGeoZones: [geoZoneId] })
                        }
                    }
                )
                menu.addItem({
                    text: `Редактировать геозону: ${geoZone.name} (${geoZone.glid})"`,
                    callback: () => {
                        this.emit('geozoneedit', { selectedGeoZones: [geoZoneId] })
                    }
                })
            }
        }
    }

    onGeoZoneClick(polygon: GeoZoneGraphic, geoZone: GeoZoneHashElement) {
        return () => {
            if (this.selected) {
                this.selected.alpha = this.opacity
            }
            this.selected = polygon
            this.selected.alpha = .7
            this.service.refreshPixiContainer()
        }
    }

    renderPolygonName(polygon: GeoZoneGraphic, geoZone: GeoZoneHashElement) {
        let text: PIXI.Text = new PIXI.Text(geoZone.name);
        const { x: boundX, y: boundY, width: boundWidth = 0, height: boundHeight = 0 } = polygon.getLocalBounds()
        text.name = 'text'
        text.style = new PIXI.TextStyle({
            fontFamily: `"PTRootUI",Arial,sans-serif`,
            fontSize: geoZone.zoneType === "cadastre" ? 13 : 24,
            fontWeight: "bold",
            lineHeight: 18,
            // fontStyle: 'italic',
            // fill : '#F7EDCA',
            // stroke : '#4a1850',
            // strokeThickness : 5,
            // dropShadow : true,
            // dropShadowColor : '#000000',
            // dropShadowAngle : Math.PI / 6,
            // dropShadowDistance : 6,
            wordWrap: true,
            wordWrapWidth: boundWidth
        })
        text.anchor.x = 0.5;
        text.zIndex = this.getZIndex()

        text.x = boundX + (boundWidth / 2)
        text.y = boundY + (boundHeight / 2)
        log.debug("Render text")
        text.visible = false
        polygon.addChild(text)
    }

    getCadastrePolyOptions(geoZone: GeoZoneHashElement, options?: GeoZoneCadastreRendererOptions): Partial<PolyDrawOptions> {
        const drawOnlyBorder = options?.fill === 'Границы участка'
        return {
            alpha: drawOnlyBorder ? 0 : 1,
            lineColor: drawOnlyBorder ? hex2number("#CD5C5C") : hex2number("000000")
        }
    }

    getDefaultPolyOptions(geoZone: GeoZoneHashElement): Partial<PolyDrawOptions> {
        const geoZoneBaseColor = hex2number(geoZoneColors[geoZone.zoneType] || geoZoneColors.field);
        const color = pipe(
            getCropDataColor.getOption(geoZone),
            option.map(hex2number),
            option.getOrElse(() => geoZoneBaseColor)
        )
        return {
            fillColor: color,
            alpha: 1,
            lineColor: hex2number("000000")
        }
    }

    renderPoly(geoZone: GeoZoneHashElement, polygonGraphic: GeoZoneGraphic, options?: GeoZoneRendererOptions) {
        if (geoZone.geometry) {
            polygonGraphic.clear()
            const config = {
                ...this.getDefaultPolyOptions(geoZone),
                ...(geoZone.zoneType === 'cadastre' ? this.getCadastrePolyOptions(geoZone, options?.cadastre) : {})
            }
            log.debug("Repaint geoZone with config", config)
            let drawer = PolygonDrawer(polygonGraphic, this.settings, config)
            drawer(geoZone.geometry.coordinates)
        } else log.warn("Geometry is unexpectedly empty for geo zone with name", geoZone.name)
    }

    redraw(geoZoneIds: string[], options?: GeoZoneRendererOptions): void {
        // TODO!
        for (const geoZoneId of geoZoneIds) {
            const geoZone = geoZoneSelectors.selectById(store.getState().geoZones, geoZoneId)
            if (geoZone) {
                const layerId = geoZoneId
                const polygonGraphic = this.container?.getChildByName(layerId) as GeoZoneGraphic
                if (!polygonGraphic) {
                    log.error("Not found geo zone graphic by id", layerId)
                } else {
                    log.debug("Update geoZone", layerId)
                    this.renderPoly(geoZone, polygonGraphic, options)
                    for (const renderer of this.children) {
                        if (renderer.isMatch(geoZone)) renderer.render([geoZoneId])
                    }
                }
            }
        }
    }

    render(geoZoneIds: EntityId[], options?: GeoZoneRendererOptions): void {
        for (const geoZoneId of geoZoneIds) {
            const geoZone = geoZoneSelectors.selectById(store.getState().geoZones, geoZoneId)
            const layerId = geoZoneId
            if (!this.container?.getChildByName(layerId as string) && geoZone) {
                if (geoZone.geometry) {
                    let polygonGraphic: GeoZoneGraphic = new PIXI.Graphics() as GeoZoneGraphic;
                    polygonGraphic.name = geoZone.glid
                    polygonGraphic.zIndex = geoZone.zoneType === "cadastre" ? this.getZIndex() - 2 : this.getZIndex()

                    polygonGraphic.interactive = true
                    polygonGraphic.buttonMode = true

                    polygonGraphic.alpha = this.opacity

                    polygonGraphic.on('click', this.onGeoZoneClick(polygonGraphic, geoZone))
                    polygonGraphic.on('rightdown', this.onGeoZoneRightClick(polygonGraphic, geoZoneId))

                    log.debug('Render geoZone with id...zIndex = ' + polygonGraphic.zIndex, layerId, geoZone.name, geoZone.zoneType)

                    this.renderPoly(geoZone, polygonGraphic, options)
                    this.renderPolygonName(polygonGraphic, geoZone)

                    this.container?.addChild(polygonGraphic)
                } else {
                    log.warn(`GeoZone with name ${geoZone.name} hasn't geometry`, geoZone)
                }
            }
        }
    }
}